/**
 * LanePicker — the Box Truck board's Origin / Destination field.
 *
 * Replaces the type-ahead on this board only. The type-ahead asked the operator to know the answer before
 * they could pick it: the menu stayed empty until something was typed, and a state was only reachable by
 * its two-letter code. Box Truck lanes are chosen by state far more often than by city, so the states are
 * now the default view — every one of them at once, as a grid of code tiles. Nothing to type, nothing to
 * scroll, one click per state and the panel stays open for the next.
 *
 * City and ZIP keep their own input at the top of the panel, because those genuinely need a search: a
 * five-digit ZIP is geocoded live (it is not in the local city dataset), anything longer than two
 * characters searches cities, and the same text also narrows the grid by state code or state name.
 *
 * SEVERAL ZIPs can be typed at once — "60601 30301, 46201" — and they STACK, the same way picking several
 * drivers does. That is not a second kind of selection: each point becomes one chip, and `buildRequests`
 * already runs one search per pinned chip and merges them into this board. Each ZIP therefore costs a
 * search of its own, including two a few blocks apart whose radii all but coincide — `pinnedPoints`
 * collapses only the SAME position (~110 m).
 *
 * What bounds that cost is `MAX_ZIPS_PER_QUERY` applied to the FIELD, not to the lookup. The lookup cap
 * alone bounds nothing: twelve ZIPs pasted, added, and pasted again are twenty-four lanes — twenty-four
 * SSE subscriptions and twenty-four DAT calls against a token pool every operator shares. So the field
 * refuses the thirteenth point and says so. (The driver picker writes `originOptions` directly, not
 * through here, and is not bounded by this — see the note on `pointsFull`.)
 *
 * DAT zones are deliberately absent. They are a second vocabulary for the same idea — a group of states —
 * and offering both asks the operator to choose between two ways of saying one thing. States alone cover
 * every lane this board searches. A zone already stored on a saved tab still works and still shows in the
 * field; it just cannot be picked here any more.
 *
 * The grid is laid out by region (Northeast, Midwest, …), so a region's states sit together, and each
 * region's header can select or clear all of them. That does not bring the second vocabulary back: what
 * lands in the selection is plain states.
 *
 * Selection semantics are shared with Load Hub's MultiLocationInput (utils/locationSelection) — a city is
 * exclusive and searched as a point with a radius, states and zones stack and are searched as areas. The
 * two pickers look nothing alike; they must never disagree about what a selection means.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { MapPin, Search, X } from 'lucide-react'
import { isUsZipCode } from '@numeo/types-core'
import { MAX_BOX_TRUCK_ZIPS_PER_REQUEST } from '@numeo/types-main-web-app'
import { US_REGIONS } from '@numeo/types'
import { cn } from 'utils/cn'
import { resolveZipCodes } from '../services/boxTruckLoadsApi'
import { LocationType, locationDataSearchV2 } from 'utils/location-search/LocationDataSearchV2'
import type { CityLocationData, StateLocationData } from 'utils/location-search/LocationDataSearchV2'
import type { LocationData } from 'features/load-hub/types/filters'
import { addCity, addState, isCityLocation, selectCity } from 'features/load-hub/utils/locationSelection'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'
import { LanePickerStateGrid, SectionLabel } from './LanePickerStateGrid'
import { summarizeBoxTruckLocations } from '../utils/boxTruckFilterSummary'
import { trackBoxTruckZipListAdded } from '../utils/analytics'
import { log } from 'utils/logger'

interface LanePickerProps {
    values: LocationData[]
    onChange: (next: LocationData[]) => void
    placeholder?: string
    error?: boolean
    /**
     * Do several points mean several searches for this leg?
     *
     * ORIGIN only. `buildRequests` fans a lane out per origin point — which is what picking several
     * drivers already does — while the destination goes through one `toLocationPayload` and searches a
     * single circle. Stacking there would show two chips and search one of them, and the live feed would
     * disagree with the refresh: `matchesLeg` accepts a load near ANY destination point, so loads near
     * the second would arrive live and vanish on the next page.
     *
     * Off by default, so a leg that has not thought about it keeps the exclusive behaviour.
     */
    multiPoint?: boolean
}

/** Below three characters a city search matches most of the dataset; states are covered by the grid instead. */
const CITY_MIN_QUERY = 3
/**
 * The ZIPs in a typed query, in order and without repeats.
 *
 * Separators are deliberately loose — comma, space, newline, semicolon — because this field is pasted
 * into as often as it is typed, and a list copied out of a spreadsheet or a message arrives in whichever
 * of those the source used. Anything that is not five digits is simply not a ZIP and leaves the query to
 * the city search, so "Chicago" still searches cities and "60601 Chicago" finds the ZIP and ignores the rest.
 *
 * Two steps, and they are different questions. The regex SCANS free text for candidates, which is this
 * field's own job — nothing else here reads a pasted sentence. Whether a candidate IS a ZIP is the
 * owner's answer (`isUsZipCode`, which `geocodeUsPostalCode` also asks before spending a provider call),
 * so this cannot start offering ZIPs the server will refuse, or refusing ones it would accept.
 */
const parseZips = (query: string): string[] => [...new Set((query.match(/\b\d{5}\b/g) ?? []).filter(isUsZipCode))]
/** The server's cap, shared rather than restated: a client cap above it turns every full paste into a
 *  400, one below it makes "only the first N were looked up" a lie. The ones past it are REPORTED, not
 *  quietly dropped — see `ignoredZips`. The wording says LOOKED UP rather than searched because this
 *  warning also renders on Destination, which looks every ZIP up and then searches exactly one. */
const MAX_ZIPS_PER_QUERY = MAX_BOX_TRUCK_ZIPS_PER_REQUEST
/** A region name is matched from three letters: anything shorter is a state code (MI, CO), and must not pull in a region. */
const REGION_MIN_QUERY = 3
/** Enough to swallow a burst of typing without making the list feel laggy. */
const SEARCH_DEBOUNCE_MS = 200
/** The grid needs more room than the trigger; the panel widens rather than wrapping two tiles per row. */
const PANEL_MIN_WIDTH = 336
/** Trigger summary before it rolls into "+N" — four codes fit the field at its narrowest. */
const VISIBLE_CHIPS = 4

export const LanePicker = ({ values, onChange, placeholder = 'City, State or ZIP', error = false, multiPoint = false }: LanePickerProps) => {
    const { open, anchorRect, containerRef, triggerRef, panelRef, openPanel, close, panelPositionStyle } = useAnchoredPopover()

    // The state list comes from the city dataset, which loads asynchronously — hold it in state rather than
    // reading it during render, or the grid paints empty on first open and never refills.
    const [states, setStates] = useState<StateLocationData[]>([])
    // Loading is the state worth naming: the grid IS the state picker, so if the dataset never arrives the
    // panel must say so rather than sit on "Loading…" forever. The fetch can genuinely fail — it pulls a CSV
    // over the network on first use.
    const [statesStatus, setStatesStatus] = useState<'loading' | 'ready' | 'failed'>('loading')
    useEffect(() => {
        let alive = true
        locationDataSearchV2
            .initialize()
            .then(() => {
                if (!alive) return
                setStates(locationDataSearchV2.searchByStateCodes(''))
                setStatesStatus('ready')
            })
            .catch(() => {
                if (alive) setStatesStatus('failed')
            })
        return () => {
            alive = false
        }
    }, [])

    const [query, setQuery] = useState('')
    /**
     * Results, and the query they ANSWER.
     *
     * Not a bare list: during the debounce and the lookups `cities` still holds the previous query's
     * results, and "Add all" acting on those adds the ZIPs the operator typed a moment ago while dropping
     * the one they just added — silently, which is the one thing this field must not do. Everything that
     * acts on results compares this query to the live one first.
     */
    const [results, setResults] = useState<{ query: string; items: CityLocationData[] }>({ query: '', items: [] })
    /** ZIPs the PROVIDER would not answer for, as opposed to ones that do not exist — a different message
     *  and a different remedy, and only the server can tell them apart. */
    const [zipFailures, setZipFailures] = useState<string[]>([])
    /** The whole request failed — a 5xx, a refused guard, a dropped connection. Distinct from
     *  `zipFailures` (which the server reported per ZIP) and from an empty result: with no state for it,
     *  the catch below left the UI saying every ZIP typed was a bad one. */
    const [lookupFailed, setLookupFailed] = useState(false)
    const [searching, setSearching] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    // Clear the transient city/ZIP query whenever the panel closes.
    useEffect(() => {
        if (!open) return
        return () => setQuery('')
    }, [open])

    const togglePanelAndFocus = () => {
        if (open) return close()

        // iOS only presents its software keyboard when focus remains inside the user's activation.
        // Commit the portalled input synchronously, then focus it before this click handler returns.
        flushSync(openPanel)
        inputRef.current?.focus({ preventScroll: true })
    }

    // Only the newest search may write results — a slow ZIP geocode must not overwrite a later city search.
    const searchSeq = useRef(0)

    useEffect(() => {
        const trimmed = query.trim()
        if (trimmed.length < CITY_MIN_QUERY) {
            setResults({ query: trimmed, items: [] })
            setZipFailures([])
            setLookupFailed(false)
            setSearching(false)
            return
        }
        const seq = ++searchSeq.current
        setSearching(true)
        setLookupFailed(false)
        const timer = setTimeout(async () => {
            try {
                // A five-digit ZIP is not in the local dataset, so it is resolved live to a point and then
                // flows through the same path as a typed city.
                const zips = parseZips(trimmed)
                if (zips.length > 0) {
                    // ONE request, whether the operator typed one ZIP or a dozen, and through the OWNER:
                    // `@numeo_private/geo` validates each answer and caches it for a week, and the server
                    // is the only place that can tell a ZIP that does not exist from a provider that would
                    // not answer. The browser geocoder this replaces could do neither, and talking to the
                    // public endpoint from every open tab is what its usage policy forbids.
                    const { resolved, failed } = await resolveZipCodes(zips.slice(0, MAX_ZIPS_PER_QUERY))
                    if (seq !== searchSeq.current) return
                    setZipFailures(failed)
                    setLookupFailed(false)
                    setResults({
                        query: trimmed,
                        items: resolved.map((place) => ({
                            id: `zip-${place.zip}`,
                            type: LocationType.CITY,
                            city: place.city || place.zip,
                            stateCode: place.state,
                            stateName: '',
                            county: '',
                            latitude: place.latitude,
                            longitude: place.longitude,
                            postalCode: place.zip,
                        })),
                    })
                    return
                }
                const found = await locationDataSearchV2.search(trimmed)
                if (seq !== searchSeq.current) return
                setResults({ query: trimmed, items: found.slice(0, 8) })
            } catch (error) {
                // Leaving the previous query's cities up would be worse than showing none: they no longer
                // match what was typed, and the operator can pick one.
                if (seq === searchSeq.current) {
                    setResults({ query: trimmed, items: [] })
                    setZipFailures([])
                    // A ZIP query that threw means the LOOKUP failed, not that the ZIPs are wrong. Without
                    // this the list branch reported every one of them as "check that ZIP" and the single
                    // branch said "Nothing matches" — both blaming the operator for a server error.
                    if (parseZips(trimmed).length > 0) {
                        setLookupFailed(true)
                        log.w('[box-truck] ZIP lookup request failed', { error: error instanceof Error ? error.message : String(error) })
                    }
                }
            } finally {
                if (seq === searchSeq.current) setSearching(false)
            }
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [query])

    // Typing narrows the grid too, by code or by name — "cal" finds CA without knowing the abbreviation. From
    // three letters the query also matches the start of a region's name and keeps its whole block: "mount"
    // shows the Mountain block. The state matches are kept apart so Enter only ever picks one of them.
    const { stateMatches, visibleStates } = useMemo(() => {
        const q = query.trim().toUpperCase()
        if (!q) return { stateMatches: states, visibleStates: states }
        const isStateMatch = (s: StateLocationData) => s.stateCode.startsWith(q) || s.stateName.toUpperCase().startsWith(q)
        const matches = states.filter(isStateMatch)
        if (q.length < REGION_MIN_QUERY) return { stateMatches: matches, visibleStates: matches }
        const regionStates = new Set(US_REGIONS.filter((r) => r.label.toUpperCase().startsWith(q)).flatMap((r) => r.states))
        return { stateMatches: matches, visibleStates: states.filter((s) => isStateMatch(s) || regionStates.has(s.stateCode)) }
    }, [states, query])

    /**
     * How many more points this field will take. Counted from `values`, so it bounds the FIELD however the
     * points got there — a paste, a second paste, or one click at a time.
     *
     * It does NOT bound the driver picker, which calls `setOriginOptions` on the page rather than coming
     * through this component; picking forty drivers is still forty lanes, exactly as it was before ZIP
     * lists existed. Bounding that is a separate decision about an existing workflow, not a detail of this
     * field — capping it here would silently drop drivers an operator deliberately ticked.
     */
    const remainingPoints = useMemo(() => (multiPoint ? MAX_ZIPS_PER_QUERY - values.filter(isCityLocation).length : 1), [multiPoint, values])
    const pointsFull = remainingPoints <= 0

    /**
     * Points STACK here, and a CLICK leaves the panel open — the same rhythm as the state grid.
     *
     * `close()` used to follow every pick because a city replaced the selection, so there was never a
     * second one to make. Now that several points are one search each, closing after the first click would
     * make the operator reopen the panel per ZIP. Enter is the other way in and does close — see `onKeyDown`.
     */
    const pickCity = useCallback(
        (city: CityLocationData) => {
            if (!multiPoint) {
                // One point per leg: replace, and close, exactly as this field behaved before.
                onChange(selectCity(city))
                setQuery('')
                close()
                return
            }
            if (pointsFull) return
            onChange(addCity(values, city, MAX_ZIPS_PER_QUERY))
            setQuery('')
        },
        [onChange, values, multiPoint, close, pointsFull]
    )

    /**
     * The results that answer what is in the field RIGHT NOW. Everything below reads this, never
     * `results.items`: a list left over from the previous keystroke is not an answer to this one.
     */
    const cities = useMemo(() => (results.query === query.trim() ? results.items : []), [results, query])

    /** A typed LIST, as opposed to one ZIP or a city search — what the add-all control is for. */
    const zipList = useMemo(() => multiPoint && parseZips(query).length > 1, [query, multiPoint])
    /** Typed but unresolved, once the lookups for THIS query have settled. */
    const missingZips = useMemo(() => {
        // `searching` alone is not enough: on the first render after a keystroke the effect has not run
        // yet, so it is still false while `results` answers the previous query. That frame paired the new
        // text with the old results and flashed "Could not place …" for ZIPs about to resolve.
        if (!zipList || searching || results.query !== query.trim()) return []
        const resolved = new Set(cities.map((c) => c.postalCode))
        const couldNotAsk = new Set(zipFailures)
        // A ZIP the provider refused is NOT "not found": it would get both messages at once, and the
        // second one blames the operator for the first one's outage.
        return parseZips(query)
            .slice(0, MAX_ZIPS_PER_QUERY)
            .filter((zip) => !resolved.has(zip) && !couldNotAsk.has(zip))
    }, [zipList, searching, cities, query, results.query, zipFailures])
    /** Typed past the cap, and therefore never looked up. Said out loud: a paste of twenty that quietly
     *  became twelve is twelve lanes the operator believes are twenty. */
    /** Not gated on `zipList`: a ZIP past the cap is absent from the results whichever leg asked, and the
     *  one the operator wanted may be the thirteenth. */
    const ignoredZips = useMemo(() => parseZips(query).slice(MAX_ZIPS_PER_QUERY), [query])

    /** Resolved, and there is room for it. The rest are reported, never dropped quietly. */
    const addable = useMemo(() => cities.slice(0, Math.max(remainingPoints, 0)), [cities, remainingPoints])
    /** Resolved but refused for want of room — a different sentence from "could not place it". */
    const noRoomFor = useMemo(() => cities.slice(Math.max(remainingPoints, 0)).map((c) => c.postalCode || c.city), [cities, remainingPoints])

    /** Add every resolved ZIP in one press — the point of typing a list. */
    const pickAllCities = useCallback(() => {
        if (addable.length === 0) return
        onChange(addable.reduce<LocationData[]>((acc, city) => addCity(acc, city, MAX_ZIPS_PER_QUERY), values))
        // Counts only, never the ZIPs: a ZIP is where a carrier's truck is, and what the aggregate has to
        // answer is whether lists are used and whether they resolve — not which places.
        trackBoxTruckZipListAdded({
            added: addable.length,
            notFound: missingZips.length,
            failed: zipFailures.length,
            // A ZIP past the lookup cap and one past the field's capacity are both "asked for, not searched".
            ignored: ignoredZips.length + noRoomFor.length,
        })
        setQuery('')
    }, [addable, noRoomFor, onChange, values, missingZips, zipFailures, ignoredZips])

    // Enter takes the obvious single answer — the one city found, or the one state left in the grid, or
    // the whole resolved list when a list is what was typed.
    //
    // A city or ZIP taken by Enter also CLOSES the panel. Typing a ZIP and pressing Enter is "this is my
    // origin", and with the panel left open the cleared query brought the whole state grid up in its place —
    // a second window the operator never asked for, which then had to be dismissed by hand. Stacking more
    // points is still one click on a row away. Nothing added (the field is full) keeps the panel and its
    // "remove one to add another" in view.
    const takeEnter = () => {
        // `zipList`, not "several ZIPs resolved": it is the one condition that already carries
        // `multiPoint`. Enter was the third door to `pickAllCities` and the only one left ungated, so on
        // the Destination field — which searches ONE circle — a pasted list stacked several chips, the
        // panel stayed open, and the add-all event fired with counts that leg never computes.
        if (zipList && cities.length > 1 && cities.every((c) => c.postalCode)) {
            // Still returns when the field is full: `pickAllCities` is a no-op then, and falling through to
            // `pickCity` would make Enter add the single first ZIP of a list the operator typed as a list.
            if (addable.length === 0) return
            pickAllCities()
            close()
            return
        }
        if (cities.length > 0) {
            if (pointsFull) return
            pickCity(cities[0])
            close()
            return
        }
        if (stateMatches.length === 1) {
            onChange(addState(values, stateMatches[0].stateCode))
            setQuery('')
        }
    }

    /**
     * The ZIP query an Enter was pressed on before its lookup answered.
     *
     * Five digits and Enter is one motion, faster than the debounce plus the round trip, so that Enter
     * found no results yet and did nothing: no chip, the panel still open, and a second Enter needed once
     * the row appeared. It is held instead and taken when the answer to THAT query lands. Typing on drops
     * it — the held Enter was for text that is no longer in the field.
     */
    const pendingEnter = useRef<string | null>(null)
    useEffect(() => {
        if (pendingEnter.current === null) return
        const trimmed = query.trim()
        if (pendingEnter.current !== trimmed) {
            pendingEnter.current = null
            return
        }
        if (searching || results.query !== trimmed) return
        pendingEnter.current = null
        takeEnter()
    })

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            close()
            return
        }
        if (e.key !== 'Enter') return
        const trimmed = query.trim()
        if (parseZips(trimmed).length > 0 && (searching || results.query !== trimmed)) {
            pendingEnter.current = trimmed
            return
        }
        takeEnter()
    }

    const summary = useMemo(() => {
        return summarizeBoxTruckLocations(values, '', VISIBLE_CHIPS) || null
    }, [values])

    return (
        <div ref={containerRef} className="relative w-full">
            <button
                ref={triggerRef}
                type="button"
                onClick={togglePanelAndFocus}
                className={cn(
                    'flex h-8 w-full items-center gap-1.5 rounded-md border bg-white px-2 text-left text-xs font-medium transition-colors dark:bg-slate-900',
                    'border-slate-300 hover:border-slate-400 dark:border-slate-600',
                    open && 'border-blue-600 ring-2 ring-blue-600/20',
                    error && '!border-red-500 ring-red-500/20'
                )}
            >
                <MapPin size={13} className="shrink-0 text-slate-400" />
                <span
                    className={cn('flex-1 truncate', summary ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500')}
                    title={summary ?? ''}
                >
                    {summary ?? placeholder}
                </span>
                {/* Spacer under the clear button, which cannot live inside this one: HTML forbids
                    interactive content inside a button, and nesting it puts the clear out of keyboard reach. */}
                {values.length > 0 && <span className="w-4 shrink-0" />}
            </button>

            {values.length > 0 && (
                <button
                    type="button"
                    aria-label="Clear"
                    onClick={() => onChange([])}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-blue-600 dark:hover:bg-slate-800"
                >
                    <X size={12} />
                </button>
            )}

            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{
                            ...panelPositionStyle,
                            width: 'auto',
                            minWidth: Math.max(anchorRect.width, PANEL_MIN_WIDTH),
                            // The panel is wider than its trigger, so the hook's left (the trigger's) can
                            // push it off-screen; pull it back inside the viewport.
                            left: Math.max(8, Math.min(anchorRect.left, window.innerWidth - Math.max(anchorRect.width, PANEL_MIN_WIDTH) - 8)),
                            maxHeight: Math.min(440, window.innerHeight - 16),
                        }}
                        className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
                    >
                        <div className="flex items-center gap-1.5 border-b border-slate-200 px-2.5 py-2 dark:border-slate-700">
                            <Search size={13} className="shrink-0 text-slate-400" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={onKeyDown}
                                placeholder="City or ZIP…"
                                className="w-full bg-transparent text-base font-medium text-foreground outline-none placeholder:text-foreground-muted min-[900px]:text-xs"
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => setQuery('')}
                                    className="shrink-0 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                >
                                    <X size={12} />
                                </button>
                            )}
                            {/* Clear sits in the search row as well as the footer: the footer is out of view
                                once the grid scrolls, and this row is always there, so nothing shifts when it appears. */}
                            {values.length > 0 && (
                                <button
                                    type="button"
                                    aria-label="Clear selection"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => onChange([])}
                                    className="shrink-0 border-l border-slate-200 pl-2 text-[11px] font-semibold text-blue-600 hover:text-blue-700 dark:border-slate-700"
                                >
                                    Clear
                                </button>
                            )}
                        </div>

                        <div className="flex flex-col gap-2 overflow-y-auto p-2.5">
                            {cities.length > 0 && (
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <SectionLabel>{zipList ? 'ZIP codes' : 'Cities'}</SectionLabel>
                                        {/* Only for a LIST. One result is one click already, and a second
                                            control beside it would just be a longer way to press it. */}
                                        {zipList && (
                                            <button
                                                type="button"
                                                onClick={pickAllCities}
                                                className="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                                            >
                                                Add all {cities.length}
                                            </button>
                                        )}
                                    </div>
                                    {cities.map((city) => (
                                        <button
                                            key={city.id}
                                            type="button"
                                            onClick={() => pickCity(city)}
                                            className="flex items-center justify-between rounded-lg px-2 py-1.5 text-left text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                                        >
                                            <span className="truncate">
                                                {city.postalCode ? `${city.postalCode} · ` : ''}
                                                {city.city}, {city.stateCode}
                                            </span>
                                            <span className="ml-2 shrink-0 text-[10px] text-slate-400">{city.postalCode ? 'ZIP' : 'City'}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {/* A ZIP the provider could not place is absent from the list above, and silence
                                there reads as "still loading". Named, so the operator can retype it. */}
                            {lookupFailed && (
                                <p className="px-2 text-[11px] text-amber-600 dark:text-amber-500">
                                    Could not reach the address service just now. Try again in a moment.
                                </p>
                            )}
                            {!lookupFailed && zipFailures.length > 0 && results.query === query.trim() && (
                                <p className="px-2 text-[11px] text-amber-600 dark:text-amber-500">
                                    Could not look up {zipFailures.join(', ')} just now — the address service did not answer. Try again in a moment.
                                </p>
                            )}
                            {!lookupFailed && zipList && missingZips.length > 0 && (
                                <p className="px-2 text-[11px] text-amber-600 dark:text-amber-500">
                                    Could not place {missingZips.join(', ')} — check {missingZips.length > 1 ? 'those ZIPs' : 'that ZIP'}.
                                </p>
                            )}
                            {ignoredZips.length > 0 && (
                                <p className="px-2 text-[11px] text-amber-600 dark:text-amber-500">
                                    Only the first {MAX_ZIPS_PER_QUERY} ZIPs were looked up — {ignoredZips.join(', ')} {ignoredZips.length > 1 ? 'were' : 'was'}{' '}
                                    left out.
                                </p>
                            )}
                            {/* The field is full, or this list would overfill it. Said BEFORE the press as
                                well as after, because each point is a whole search and an operator who
                                cannot see the ceiling finds it by hitting it. */}
                            {multiPoint && pointsFull && (
                                <p className="px-2 text-[11px] text-amber-600 dark:text-amber-500">
                                    {MAX_ZIPS_PER_QUERY} origin points is the most this board searches at once — remove one to add another.
                                </p>
                            )}
                            {multiPoint && !pointsFull && noRoomFor.length > 0 && (
                                <p className="px-2 text-[11px] text-amber-600 dark:text-amber-500">
                                    Room for {remainingPoints} more — {noRoomFor.join(', ')} will not be added.
                                </p>
                            )}

                            {visibleStates.length > 0 && (
                                <LanePickerStateGrid states={visibleStates} loadedStates={states} values={values} onChange={onChange} />
                            )}

                            {/* Only a dead end deserves a message: a typed query that matched no city and no
                                state, and is not still resolving. A lookup that FAILED is not a dead end —
                                it has its own message above, and "Nothing matches 60601" beside it would
                                tell the operator their ZIP is wrong when the service simply did not answer. */}
                            {!searching && !lookupFailed && zipFailures.length === 0 && cities.length === 0 && visibleStates.length === 0 && (
                                <p className="px-1 py-3 text-center text-xs text-slate-400">
                                    {statesStatus === 'failed'
                                        ? 'Could not load the state list — search by city or ZIP, or reopen to retry.'
                                        : statesStatus === 'loading' && !query
                                          ? 'Loading states…'
                                          : `Nothing matches “${query.trim()}”`}
                                </p>
                            )}
                        </div>

                        {values.length > 0 && (
                            <div className="flex items-center justify-between border-t border-slate-200 px-2.5 py-1.5 dark:border-slate-700">
                                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{values.length} selected</span>
                                <button type="button" onClick={() => onChange([])} className="text-[11px] font-semibold text-blue-600 hover:text-blue-700">
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>,
                    document.body
                )}
        </div>
    )
}
